import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { propertyImageUrl } from '../lib/propertyImage';
import { Property, QuoteResponse } from '../types';
import './SearchPage.css';

// ------------------------------------------------------------
// 日付ユーティリティ（ローカルタイムで YYYY-MM-DD を扱う）
// ------------------------------------------------------------
function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function parseISO(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function nightsBetween(checkin: string, checkout: string): number {
  return Math.round(
    (parseISO(checkout).getTime() - parseISO(checkin).getTime()) / 86400000
  );
}

// 各物件の検索結果（1物件 = 1 quote 呼び出しの結果）
interface SearchResult {
  property: Property;
  status: 'available' | 'unavailable' | 'error';
  quote?: QuoteResponse;
  message?: string; // 満室・エラー時の理由
}

function SearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ---------- 検索条件 ----------
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayISO = toISO(today);

  const [checkin, setCheckin] = useState<string>('');
  const [checkout, setCheckout] = useState<string>('');
  const [guestCount, setGuestCount] = useState<number>(2);

  // ---------- 物件マスタ ----------
  const [properties, setProperties] = useState<Property[]>([]);
  const [propLoading, setPropLoading] = useState(true);
  const [propError, setPropError] = useState<string | null>(null);

  // ---------- 検索結果 ----------
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ---------- 物件一覧を取得 ----------
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/properties');
        if (!res.ok) throw new Error('Failed to fetch properties');
        const data: Property[] = await res.json();
        setProperties(data);
      } catch (err) {
        setPropError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setPropLoading(false);
      }
    })();
  }, []);

  const fmtMoney = (n: number, currency = 'JPY') =>
    new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(n);

  // ---------- 入力バリデーション ----------
  const nights =
    checkin && checkout ? nightsBetween(checkin, checkout) : 0;
  const datesValid =
    !!checkin && !!checkout && nights >= 1 && checkin >= todayISO;

  // ---------- 空室を検索（3物件へ並列 quote） ----------
  const handleSearch = async () => {
    setFormError(null);
    if (!checkin || !checkout) {
      setFormError(t('search.errorSelectDates'));
      return;
    }
    if (nights < 1) {
      setFormError(t('search.errorCheckoutAfterCheckin'));
      return;
    }
    if (checkin < todayISO) {
      setFormError(t('search.errorPastDate'));
      return;
    }

    setSearching(true);
    setResults(null);

    // 各物件に対して見積もりを並列取得。
    // quote が成功 → 空室あり＋料金。409/その他 → 満室 or 条件不一致。
    const settled = await Promise.all(
      properties.map(async (property): Promise<SearchResult> => {
        try {
          const res = await fetch('/api/reservations/quote', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              property_id: property.id,
              checkin_date: checkin,
              checkout_date: checkout,
              guest_count: guestCount,
            }),
          });
          const data = await res.json();
          if (res.ok) {
            return {
              property,
              status: 'available',
              quote: data as QuoteResponse,
            };
          }
          // 空室なし・最低泊数未満・人数超過などはすべて「予約不可」として扱う
          return {
            property,
            status: 'unavailable',
            message: data?.error || t('property.unavailable'),
          };
        } catch (err) {
          return {
            property,
            status: 'error',
            message:
              err instanceof Error ? err.message : t('common.error'),
          };
        }
      })
    );

    setResults(settled);
    setSearching(false);
  };

  // ---------- 「予約する」→ 既存のゲスト情報ページへ ----------
  const handleReserve = (r: SearchResult) => {
    if (r.status !== 'available' || !r.quote) return;
    navigate('/booking/guest', {
      state: {
        property: r.property,
        checkin,
        checkout,
        guestCount,
        quote: r.quote,
      },
    });
  };

  if (propLoading) return <div className="loading">{t('common.loading')}</div>;
  if (propError)
    return (
      <div className="error">
        {t('common.error')}: {propError}
      </div>
    );

  const maxGuestsAll = properties.reduce(
    (m, p) => Math.max(m, p.max_guests),
    10
  );

  return (
    <div className="search-page">
      <div className="container search-container">
        <h1 className="search-title">{t('search.title')}</h1>
        <p className="search-subtitle">{t('search.subtitle')}</p>

        {/* ============ 検索フォーム ============ */}
        <section className="search-form">
          <div className="search-field">
            <label htmlFor="s-checkin">{t('search.checkin')}</label>
            <input
              id="s-checkin"
              type="date"
              min={todayISO}
              value={checkin}
              onChange={(e) => {
                setCheckin(e.target.value);
                // チェックイン >= チェックアウトになったらチェックアウトをクリア
                if (checkout && e.target.value >= checkout) {
                  setCheckout('');
                }
              }}
            />
          </div>

          <div className="search-field">
            <label htmlFor="s-checkout">{t('search.checkout')}</label>
            <input
              id="s-checkout"
              type="date"
              min={checkin || todayISO}
              value={checkout}
              onChange={(e) => setCheckout(e.target.value)}
            />
          </div>

          <div className="search-field">
            <label htmlFor="s-guests">{t('search.guests')}</label>
            <select
              id="s-guests"
              value={guestCount}
              onChange={(e) => setGuestCount(Number(e.target.value))}
            >
              {Array.from({ length: maxGuestsAll }, (_, i) => i + 1).map(
                (n) => (
                  <option key={n} value={n}>
                    {n} {t('booking.guests')}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="search-field search-submit">
            <button
              className="btn btn-primary search-btn"
              onClick={handleSearch}
              disabled={!datesValid || searching}
            >
              {searching ? t('search.searching') : t('search.searchButton')}
            </button>
          </div>
        </section>

        {formError && <p className="search-error">{formError}</p>}

        {nights > 0 && (
          <p className="search-nights-note">
            {nights} {t('booking.nights')} / {guestCount} {t('booking.guests')}
          </p>
        )}

        {/* ============ 検索結果 ============ */}
        {searching && (
          <div className="search-loading">{t('search.searching')}</div>
        )}

        {results && !searching && (
          <section className="results">
            <h2 className="results-heading">{t('search.availableHomes')}</h2>
            <div className="results-grid">
              {results.map((r) => (
                <div
                  key={r.property.id}
                  className={`result-card ${
                    r.status === 'available' ? 'is-available' : 'is-unavailable'
                  }`}
                >
                  <div className="result-image">
                    <img
                      src={propertyImageUrl(r.property.id)}
                      alt={r.property.name}
                    />
                  </div>

                  <div className="result-body">
                    <h3 className="result-name">{r.property.name}</h3>

                    {r.status === 'available' && r.quote ? (
                      <>
                        <div className="result-status available">
                          <span className="dot" /> {t('property.available')}
                        </div>
                        <div className="result-price">
                          {fmtMoney(r.quote.total_price, r.quote.currency)}
                          <span className="result-price-note">
                            {' '}
                            / {r.quote.nights} {t('booking.nights')}（
                            {t('booking.total')}）
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="result-status unavailable">
                          <span className="dot" /> {t('property.unavailable')}
                        </div>
                        {r.message && (
                          <div className="result-msg">{r.message}</div>
                        )}
                      </>
                    )}

                    <div className="result-actions">
                      <Link
                        to={`/property/${r.property.id}`}
                        className="btn btn-outline"
                      >
                        {t('search.viewDetails')}
                      </Link>
                      <button
                        className="btn btn-primary"
                        disabled={r.status !== 'available'}
                        onClick={() => handleReserve(r)}
                      >
                        {t('search.reserve')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {results.every((r) => r.status !== 'available') && (
              <p className="results-empty">{t('search.noAvailable')}</p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default SearchPage;
